import { Avatar, Col, Form, Row, Select, Tag, Typography } from "antd";
import { debounce } from "lodash";
import {
  fetchArtistByName,
  fetchLabelByName,
  fetchLabelDetails,
  fetchLabelInfo,
  fetchTrackPopularityHistory,
} from "radar/Service/RadarService";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { Charts, FieldSelect, Loader } from "shared/components";
import { RADAR_SELECTED_LABEL } from "shared/constants/LocalStorageConsts";
import LabelInfoTables from "./LabelInfoTables";
import { SearchOutlined } from "@ant-design/icons";
import "./LabelProfile.scss";
import CardHeader from "shared/components/card/CardHeader";
import { historyTrackColors } from "../artist-profile/ArtistProfile";
import moment from "moment";
import { TableRowSelection } from "antd/lib/table/interface";
import {
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";

const { Simple, NotFoundLoader } = Loader;

const initial = () => {
  const savedFilter = localStorage.getItem(RADAR_SELECTED_LABEL);
  return { savedFilter };
};
const filter = {
  offset: 0,
  limit: 1000,
};

const LabelProfile: React.FC<RouteComponentProps> = (props) => {
  const { history } = props;
  const { t, i18n } = useTranslation("radar");

  const { savedFilter } = initial();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingLabels, setLoadingLabels] = useState<boolean>(false);
  const [loadingArtist, setLoadingArtist] = useState<boolean>(false);
  const [labelOptions, setLabelOptions] = useState<any>([]);
  const [artistsOptions, setArtistsOptions] = useState<any>([]);
  const [selectedLabel, setSelectedLabel] = useState<any>(savedFilter);
  const [labelInfo, setLabelInfo] = useState<any>();
  const [labelDetail, setLabelDetail] = useState<any>();
  const [selectedTrend, setSelectedTrend] = useState<string>("releaseTrend");
  const [artistReleaseTrend, setArtistReleaseTrend] = useState<any>({});
  const [artistTrackTrend, setArtistTrackTrend] = useState<any>({});
  const [trackPopularityRaw, setTrackPopularityRaw] = useState<any>();
  const { genreByKeys } = useSelector((state: any) => state.genres);
  const countryBykeys = useSelector(
    (state: any) => state.countries.countryByKeys
  );
  const [tracksPopularityHistory, setTracksPopularityHistory] = useState<any>({
    categories: [],
    series: [],
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<any>([]);

  const getLabels = async (term: string) => {
    const cleanTerm = term.replace(/[/\\#,+()$~%.'":*?<>{}]/g, "");
    if (cleanTerm) {
      setLoadingLabels(true);
      setLabelOptions([]);
      const response: any = await fetchLabelByName(cleanTerm);
      setLabelOptions(
        response?.map((option: any) => ({
          label: option,
          value: option,
        })) || []
      );
      setLoadingLabels(false);
    }
  };

  const getArtists = async (artistNameTerm: string) => {
    if (artistNameTerm.replace(/[/\\#,+()$~%.'":*?<>{}]/g, "")) {
      setLoadingArtist(true);
      setArtistsOptions([]);
      const response = await fetchArtistByName({ artistNameTerm });
      if (response?.length > 0)
        setArtistsOptions(
          response.map(({ artistIdStrm, artistName, imagesSpotify }: any) => ({
            id: artistIdStrm,
            name: artistName,
            image: (imagesSpotify && imagesSpotify[2]?.url) || "",
          }))
        );
      setLoadingArtist(false);
    }
  };

  const onSelectLabel = (value: any) => {
    setSelectedLabel(value);
  };

  const onChangeTrend = (value: string) => {
    setSelectedTrend(value);
  };

  const clearLabelInfoDetails = () => {
    setTrackPopularityRaw(undefined);
    setLabelInfo(undefined);
    setLabelDetail(undefined);
    setTracksPopularityHistory(undefined);
    setArtistReleaseTrend({});
    setArtistTrackTrend({});
  };

  const getLabelDetails = useCallback(() => {
    const requestLabelDetails = async () => {
      const {
        genresStrm = [],
        country = [],
        artistIdStrmList = [],
      } = form.getFieldsValue(true);
      setLoading(true);
      const response = await fetchLabelDetails({
        labelName: selectedLabel,
        genresStrm,
        country,
        artistIdStrmList,
        ...filter,
      });
      if (response) {
        const { releasesPolynomialCurve, tracksPolynomialCurve, ...labelInfo } =
          response;
        setLabelDetail(labelInfo);

        setArtistReleaseTrend({
          scatterData: response.releasesPolynomialCurve.data,
          lineData: response.releasesPolynomialCurve.polynomialCurve,
        });
        setArtistTrackTrend({
          scatterData: tracksPolynomialCurve.data,
          lineData: tracksPolynomialCurve.polynomialCurve,
        });

        setSelectedRowKeys(
          response.tracks.slice(0, 5).map((track: any) => track.trackId)
        );
      }
      setLoading(false);
    };
    requestLabelDetails();
  }, [form, selectedLabel]);

  const onFinishFilter = async () => {
    getLabelDetails();
  };

  const onClickClear = () => {
    form.resetFields();
    getLabelDetails();
  };

  const onSelectChange = (selectedKeys: any) => {
    if (selectedKeys.length > 5) {
      const selectedTracks = selectedKeys.slice(1);

      setSelectedRowKeys(selectedTracks);
    } else {
      setSelectedRowKeys(selectedKeys);
    }
  };

  const formatIndexNumber = (index: number) =>
    (index && index.toFixed(1)) || "-";

  const mapItemToLabelValue = (item: any, i18nList: any) => ({
    label: i18nList[item][`${i18n.language}`],
    value: item,
  });

  const returnListLimit = (list: any, length: number) =>
    list && list.length > 0 && list.length < length ? list : [];

  const rowSelection: TableRowSelection<any> = {
    selectedRowKeys,
    onChange: onSelectChange,
    hideSelectAll: true,
    renderCell(_, record, __, originNode) {
      const index = selectedRowKeys.indexOf(record.trackId);
      return (
        <div className={historyTrackColors[index]?.className || ""}>
          {originNode}
        </div>
      );
    },
  };

  useEffect(() => {
    const getLabelInfo = async () => {
      const response = await fetchLabelInfo({ labelName: selectedLabel });
      if (response) {
        setLabelInfo(response[0]);
      }
    };
    clearLabelInfoDetails();
    if (selectedLabel) {
      localStorage.setItem(RADAR_SELECTED_LABEL, selectedLabel);
      getLabelInfo();
      getLabelDetails();
    }
  }, [selectedLabel, getLabelDetails]);

  useEffect(() => {
    const getTracksPopularityHistory = async () => {
      const response = await fetchTrackPopularityHistory(selectedRowKeys);
      setTrackPopularityRaw(response);
    };

    if (selectedRowKeys.length > 0) {
      getTracksPopularityHistory();
    }
  }, [selectedRowKeys]);

  useEffect(() => {
    if (trackPopularityRaw) {
      setTracksPopularityHistory({
        categories:
          trackPopularityRaw.categorioes || trackPopularityRaw.categories,
        series: trackPopularityRaw.series.map((serie: any) => ({
          name: labelDetail.tracks.find(
            (track: any) => track.trackId === serie.trackId
          )?.trackName,
          data: serie.data,
        })),
      });
    }
  }, [trackPopularityRaw, labelDetail]);

  useEffect(() => {
    dataLayerViewedEvent({
      ...tagManagerEvent.Profile.S4L_LABEL_PROFILE,
    });
  }, []);

  return (
    <div className="radar-label-profile radar-home">
      <Simple hidden={!loading} size="large" fontSize={72} />
      <Row gutter={[32, 32]}>
        <Col xs={24}>
          <Row>
            <Col xs={12}>
              <Typography.Title style={{ color: "white" }}>
                {t("pages.LabelProfile.title")}
              </Typography.Title>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <Typography.Title level={3} style={{ color: "white" }}>
                {t("pages.LabelProfile.overview")}
              </Typography.Title>
            </Col>
          </Row>
        </Col>
        <Col xs={24}>
          <CardHeader>
            <Row justify="center" style={{ padding: "20px 16px" }} gutter={16}>
              <Col xs={4}>
                <span className="overview-label">
                  {t("pages.LabelProfile.label")}
                </span>
                <div className="selecao-artista">
                  <Select
                    className="radar-label-profile-select"
                    style={{ width: "100%" }}
                    onSearch={debounce(getLabels, 1000)}
                    onSelect={onSelectLabel}
                    notFoundContent={<NotFoundLoader loading={loadingLabels} />}
                    suffixIcon={<SearchOutlined />}
                    options={labelOptions}
                    value={selectedLabel}
                    showSearch
                    filterOption={false}
                    placeholder={t("pages.LabelProfile.labelPlaceholder")}
                  />
                </div>
              </Col>
              <Col xs={4}>
                <span className="overview-label">
                  {t("pages.LabelProfile.meanPopularity")}
                </span>
                <span className="overview-value">
                  {formatIndexNumber(labelInfo?.popularityIndex)}
                </span>
              </Col>
              <Col xs={4}>
                <span className="overview-label">
                  {t("pages.LabelProfile.meanConcentration")}
                </span>
                <span className="overview-value">
                  {formatIndexNumber(labelInfo?.popularityConcentrationIndex)}
                </span>
              </Col>
              <Col xs={4}>
                <span className="overview-label">
                  {t("pages.LabelProfile.releases")}
                </span>
                <span className="overview-value">
                  {labelInfo?.releaseCnt || "-"}
                </span>
              </Col>
              <Col xs={4}>
                <span className="overview-label">
                  {t("pages.LabelProfile.lastYearReleases")}
                </span>
                <span className="overview-value">
                  {labelInfo?.release12MCnt || "-"}
                </span>
              </Col>
              <Col xs={4}>
                <span className="overview-label">
                  {t("pages.LabelProfile.totalArtists")}
                </span>
                <span className="overview-value">
                  {labelInfo?.artistCnt || "-"}
                </span>
              </Col>
            </Row>
          </CardHeader>
        </Col>
        <Col hidden={!selectedLabel} xs={24}>
          <Form
            form={form}
            onFinish={onFinishFilter}
            className="filtro"
            layout="vertical"
          >
            <CardHeader style={{ minHeight: 100 }}>
              <Row
                gutter={16}
                justify="center"
                style={{ padding: "20px 16px" }}
              >
                <Col xs={6}>
                  <Form.Item
                    name="genresStrm"
                    label={t("pages.LabelProfile.genre")}
                  >
                    <FieldSelect
                      mode="multiple"
                      dataList={
                        labelInfo?.genresStrm.map((item: any) =>
                          mapItemToLabelValue(item, genreByKeys)
                        ) || []
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={6}>
                  <Form.Item
                    name="country"
                    label={t("pages.LabelProfile.country")}
                  >
                    <FieldSelect
                      mode="multiple"
                      optionFilterProp="children"
                      dataList={
                        labelInfo?.countries
                          .map((item: any) =>
                            mapItemToLabelValue(item, countryBykeys)
                          )
                          .sort((a: any, b: any) =>
                            a.value === "BR"
                              ? -1
                              : a.label > b.label
                              ? 1
                              : a.label < b.label
                              ? -1
                              : 0
                          ) || []
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={6}>
                  <Form.Item
                    name="artistIdStrmList"
                    label={t("pages.LabelProfile.artist")}
                  >
                    <Select
                      filterOption={false}
                      tagRender={tagRender}
                      placeholder={t("pages.ArtistsRanking.selectArtists")}
                      onSearch={debounce(getArtists, 1000)}
                      style={{ width: "100% " }}
                      mode="multiple"
                      maxTagCount="responsive"
                      notFoundContent={
                        <Loader.NotFoundLoader loading={loadingArtist} />
                      }
                    >
                      {artistsOptions.map((artist: any) => (
                        <Select.Option
                          value={artist.id}
                          key={artist.id}
                          label={artist.name}
                        >
                          {artist.image && (
                            <Avatar
                              style={{ marginRight: 6, width: 25, height: 25 }}
                              src={artist.image}
                            />
                          )}
                          {artist.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={2} offset={4}>
                  <div className="filter-button ">
                    <span className="delete" onClick={onClickClear}>
                      {t("pages.ArtistsRanking.delete")}
                    </span>
                    <button className="apply">
                      {t("pages.ArtistsRanking.filter")}
                    </button>
                  </div>
                </Col>
              </Row>
            </CardHeader>
          </Form>
        </Col>
        <Col hidden={!selectedLabel} xs={24}>
          {/* <Row>
            <Col lg={12} xs={12} style={{ paddingLeft: 32 }}>
              <Typography.Title level={3} style={{ color: "white" }}>
                {t("pages.LabelProfile.releases")}
              </Typography.Title>
            </Col>

            <Col lg={12} xs={12} style={{ textAlign: "end" }}>
              <Typography.Title
                style={{ color: "#fff", margin: "10px" }}
                level={5}
              >
                {t("pages.ArtistProfile.latamDisclaimer")}
              </Typography.Title>
            </Col>
          </Row> */}

          <CardHeader
            header={
              <div style={{ padding: 12 }}>
                <Row justify="end">
                  <Col
                    xs={24}
                    style={{
                      display: "flex",
                      gap: 12,
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    {selectedTrend !== "releaseTrend" && (
                      <Typography.Title
                        style={{ color: "#fff", margin: "10px" }}
                        level={5}
                      >
                        {t("pages.LabelProfile.latamDisclaimer")}
                      </Typography.Title>
                    )}
                    <Select
                      onChange={onChangeTrend}
                      value={selectedTrend}
                      style={{ minWidth: 310, marginLeft: 24 }}
                    >
                      <Select.Option value="releaseTrend">
                        {t("pages.ArtistProfile.releaseTrend")}
                      </Select.Option>
                      <Select.Option value="trackTrend">
                        {t("pages.ArtistProfile.trackTrend")}
                      </Select.Option>
                      <Select.Option value="trackPopularityHistory">
                        {t("pages.ArtistProfile.trackPopularityHistory")}
                      </Select.Option>
                    </Select>
                    {/* <Popover
                      content={() => (
                        <div style={{ width: 700 }}>
                          {selectedTrend === "trackPopularityHistory"
                            ? t(
                                "pages.ArtistProfile.disclaimerTrackPopularityHistoryTooltip"
                              )
                            : selectedTrend === "trackTrend"
                            ? t("pages.ArtistProfile.disclaimerTrackPopularity")
                            : t(
                                "pages.ArtistProfile.disclaimerReleasePopularity"
                              )}
                        </div>
                      )}
                      placement="right"
                      showArrow={false}
                    >
                      <InfoCircleOutlined style={{ color: "#fff" }} />
                    </Popover> */}
                  </Col>
                </Row>
              </div>
            }
          >
            <div className="hidden-charts">
              {selectedTrend === "releaseTrend" && (
                <>
                  <Charts.ScatterChart
                    {...artistReleaseTrend}
                    tooltip={{
                      points: returnListLimit(
                        artistReleaseTrend?.scatterData,
                        500
                      ),
                      title: t("pages.ArtistProfile.release"),
                      name: t("pages.ArtistProfile.name"),
                      release: t("pages.ArtistProfile.releaseDate"),
                      ageInMonths: t("pages.LabelProfile.ageInDays"),
                      popularity: t("pages.ArtistProfile.popularity"),
                      artistName: t("pages.LabelProfile.artistName"),
                      type: t("pages.ArtistProfile.type"),
                    }}
                    yAxisLabelText={t("pages.ArtistProfile.popularity")}
                    xAxisLabelText={t("pages.LabelProfile.releaseAgeInDays")}
                    customLegendItems={[
                      t("pages.ArtistProfile.releaseTrendLegend"),
                    ]}
                    ageMultiple={30}
                    markersSize={[
                      returnListLimit(artistReleaseTrend?.scatterData, 500)
                        .length > 0
                        ? 6
                        : 2,
                      0,
                    ]}
                    containerHeight={500}
                  />
                </>
              )}
              {selectedTrend === "trackTrend" && (
                <>
                  <Charts.ScatterChart
                    {...artistTrackTrend}
                    tooltip={{
                      points: returnListLimit(
                        artistTrackTrend.scatterData,
                        500
                      ),
                      title: t("pages.ArtistProfile.track"),
                      name: t("pages.ArtistProfile.name"),
                      release: t("pages.ArtistProfile.releaseDate"),
                      ageInMonths: t("pages.ArtistProfile.ageInMonths"),
                      popularity: t("pages.ArtistProfile.popularity"),
                      type: `${t("pages.ArtistProfile.releaseType")}`,
                    }}
                    yAxisLabelText={t("pages.ArtistProfile.popularity")}
                    xAxisLabelText={t("pages.ArtistProfile.trackAgeInMonths")}
                    containerHeight={470}
                    showLegend={false}
                  />
                  <div
                    className="apexcharts-legend apexcharts-align-left apx-legend-position-bottom"
                    style={{
                      paddingLeft: 6,
                      paddingBottom: 16,
                      display: "flex",
                      gap: 8,
                    }}
                  >
                    <div className="apexcharts-legend-series">
                      <span
                        className="apexcharts-legend-marker"
                        style={{
                          background: "#3791e3",
                          height: 12,
                          width: 12,
                          borderWidth: 0,
                          borderRadius: 12,
                          display: "inline-block",
                          marginRight: 3,
                        }}
                      ></span>
                      <span
                        className="apexcharts-legend-text"
                        style={{
                          color: "#fff",
                          fontSize: 12,
                        }}
                      >
                        {t("pages.ArtistProfile.trackTrendLgend")}
                      </span>
                    </div>
                    <div className="apexcharts-legend-series">
                      <span
                        className="apexcharts-legend-marker"
                        style={{
                          background: "#EC0190",
                          height: 12,
                          width: 12,
                          borderWidth: 0,
                          borderRadius: 12,
                          display: "inline-block",
                          marginRight: 3,
                        }}
                      ></span>
                      <span
                        className="apexcharts-legend-text"
                        style={{
                          color: "#fff",
                          fontSize: 12,
                        }}
                      >
                        {t("pages.ArtistProfile.trackTrendFeat")}
                      </span>
                    </div>
                  </div>
                </>
              )}
              {selectedTrend === "trackPopularityHistory" && (
                <>
                  <Charts.LineChart
                    {...tracksPopularityHistory}
                    color={historyTrackColors.map((color) => color.color)}
                    xaxisLabelFormat="dd/MM/yy"
                    xaxisType="datetime"
                    xLabelFormatter={(val) => moment(val).format("DD/MM/YYYY")}
                    animation={false}
                    height={500}
                  />
                </>
              )}
            </div>
          </CardHeader>

          <LabelInfoTables
            {...labelDetail}
            history={history}
            rowSelection={rowSelection}
            selectedTrend={selectedTrend}
          />
        </Col>
      </Row>
    </div>
  );
};

const tagRender: any = (props: any) => {
  const { label, closable, onClose } = props;
  const onPreventMouseDown = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <Tag
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      style={{ marginRight: 3, marginBottom: 3 }}
    >
      {label}
    </Tag>
  );
};
export default LabelProfile;
